import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { Internal } from '../../decorators/app.decorators';
import { FetchFeatureFlags } from '../../../store/feature-flags/feature-flags.actions';
import { FeatureFlagsState } from '../../../store/feature-flags/feature-flags.state';

/**
 * Fetches feature flags from the backend on every app load and exposes
 * synchronous lookups against the cached NGXS state.
 */
@Internal()
@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {
  constructor(private store: Store) {}

  /**
   * Always re-fetches, so a flag or override changed on the backend takes
   * effect on the next page load. Reusing the localStorage copy instead kept a
   * user on stale flags until they logged out - a session that expires never
   * goes through Logout, so in practice that could be weeks.
   *
   * @param userId identity of the logged-in user. If the fetch fails, the
   *   cached flags are kept only when they belong to this same user (see
   *   FeatureFlagsState.fetchFeatureFlags).
   */
  loadFeatureFlags(userId: string): Promise<void> {
    return firstValueFrom(this.store.dispatch(new FetchFeatureFlags(userId)));
  }

  isFeatureFlagEnabled(featureFlagName: string): boolean {
    const flags = this.store.selectSnapshot(FeatureFlagsState.getFeatureFlags);
    return flags?.find(flag => flag.name === featureFlagName)?.enabled ?? false;
  }

  isStorageClustersFeatureEnable(): boolean {
    return this.isFeatureFlagEnabled('STORAGE_CLUSTERS');
  }

  isAiAssessmentFeatureEnabled(): boolean {
    return this.isFeatureFlagEnabled('AI_ASSESSMENT');
  }

  isAiAssistantFeatureEnabled(): boolean {
    return this.isFeatureFlagEnabled('AI_ASSISTANT');
  }
}
